import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import logo from './ap-logo-4.png';
import './MealDetail.css';

const ACOLS = ['gluten','dairy','eggs','peanuts','nuts','soya','sesame','celery','mustard','sulphites'];
const ACOL_HEADS = { gluten:'Gluten', dairy:'Dairy', eggs:'Eggs', peanuts:'Peanuts', nuts:'Tree Nuts', soya:'Soya', sesame:'Sesame', celery:'Celery', mustard:'Mustard', sulphites:'Sulphites' };

const ALLERGEN_KW = {
  gluten:    ['wheat','flour','barley','rye','oats','spelt','kamut','gluten','semolina','breadcrumb','bulgur','durum','seitan'],
  dairy:     ['milk','cream','butter','cheese','lactose','dairy','whey','casein','ghee','paneer','yoghurt','yogurt','curd'],
  eggs:      ['egg','eggs','albumen','mayonnaise','mayo'],
  peanuts:   ['peanut','peanuts','groundnut','groundnuts'],
  nuts:      ['almond','walnut','cashew','hazelnut','pistachio','pecan','brazil','pine nut','macadamia','nut'],
  soya:      ['soy','soya','tofu','tempeh','edamame','miso'],
  sesame:    ['sesame','tahini'],
  celery:    ['celery','celeriac'],
  mustard:   ['mustard'],
  sulphites: ['sulphite','sulphites','sulfite','sulfites'],
};

const IngredientsList = ({ ingredients, allergenRow }) => {
  if (!ingredients || !ingredients.trim()) return null;

  const activeAllergens = ACOLS.filter(key => allergenRow && allergenRow[key] === true);
  const boldWords = activeAllergens.flatMap(ak => ALLERGEN_KW[ak] || []);

  const parts = ingredients.split(',').map(s => s.trim()).filter(Boolean);

  const renderPart = (ingredient, idx) => {
    const lower = ingredient.toLowerCase();
    const isAllergen = boldWords.some(w => lower.includes(w.toLowerCase()));
    return (
      <React.Fragment key={idx}>
        {idx > 0 && <span className="md-ing-comma">, </span>}
        {isAllergen
          ? <strong className="md-ing-allergen">{ingredient}</strong>
          : <span>{ingredient}</span>
        }
      </React.Fragment>
    );
  };

  return (
    <div className="md-ingredients-section">
      <div className="md-section-h">Ingredients</div>
      {activeAllergens.length > 0 && (
        <p className="md-ing-note">
          Allergens are highlighted in <strong>bold</strong> as required by UK food labelling regulations.
        </p>
      )}
      <p className="md-ing-text">
        {parts.map(renderPart)}
        {parts.length > 0 && <span>.</span>}
      </p>
      {activeAllergens.length > 0 && (
        <div className="md-ing-badge-row">
          <span className="md-ing-badge-label">Contains:</span>
          {activeAllergens.map(ak => (
            <span key={ak} className="md-ing-badge">{ACOL_HEADS[ak]}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const toSlug = name => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const NutritionBox = ({ nutrition }) => {
  const n = nutrition || {};
  const hasAny = n.calories || n.protein || n.carbs || n.fat || n.fibre || n.sugar || n.salt;
  return (
    <div className="md-nutrition-box">
      <div className="md-nutrition-header">
        <h3>Nutrition Facts</h3>
        {n.serving_size && <div className="md-nutrition-serving">Per serving · {n.serving_size}</div>}
      </div>
      <div className="md-nutrition-divider" />
      {hasAny ? (
        <>
          {n.calories && (
            <>
              <div className="md-nutrition-row bold">
                <span className="md-nutrition-label">Energy</span>
                <span className="md-nutrition-val">{n.calories} kcal</span>
              </div>
              <div className="md-nutrition-divider-sm" />
            </>
          )}
          {n.fat && (
            <div className="md-nutrition-row bold">
              <span className="md-nutrition-label">Fat</span>
              <span className="md-nutrition-val">{n.fat}g</span>
            </div>
          )}
          {n.saturates && (
            <div className="md-nutrition-row sub">
              <span className="md-nutrition-label">of which saturates</span>
              <span className="md-nutrition-val">{n.saturates}g</span>
            </div>
          )}
          {n.carbs && (
            <div className="md-nutrition-row bold">
              <span className="md-nutrition-label">Carbohydrates</span>
              <span className="md-nutrition-val">{n.carbs}g</span>
            </div>
          )}
          {n.sugar && (
            <div className="md-nutrition-row sub">
              <span className="md-nutrition-label">of which sugars</span>
              <span className="md-nutrition-val">{n.sugar}g</span>
            </div>
          )}
          {n.protein && (
            <div className="md-nutrition-row bold">
              <span className="md-nutrition-label">Protein</span>
              <span className="md-nutrition-val">{n.protein}g</span>
            </div>
          )}
          {n.fibre && (
            <div className="md-nutrition-row bold">
              <span className="md-nutrition-label">Fibre</span>
              <span className="md-nutrition-val">{n.fibre}g</span>
            </div>
          )}
          {n.salt && (
            <div className="md-nutrition-row bold">
              <span className="md-nutrition-label">Salt</span>
              <span className="md-nutrition-val">{n.salt}g</span>
            </div>
          )}
        </>
      ) : (
        <div className="md-nutrition-empty">Nutritional information not yet available.</div>
      )}
    </div>
  );
};

const AllergenBox = ({ matrix, mealName, note }) => {
  const row = matrix.find(r => toSlug(r.meal) === toSlug(mealName || ''));
  if (!row) return null;

  const allergens = ACOLS.map(key => ({ key, label: ACOL_HEADS[key], val: row[key] }));
  const present = allergens.filter(a => a.val === true);
  const maybe   = allergens.filter(a => a.val === 'may');
  const absent  = allergens.filter(a => !a.val);

  return (
    <div className="md-allergen-box">
      <div className="md-allergen-header">
        <h3>Allergen Information</h3>
      </div>
      <div className="md-allergen-grid">
        {present.map(a => (
          <div key={a.key} className="md-allergen-item">
            <div className="md-a-badge md-a-yes">✓</div>
            <span className="md-a-name">{a.label}</span>
          </div>
        ))}
        {maybe.map(a => (
          <div key={a.key} className="md-allergen-item">
            <div className="md-a-badge md-a-may">M</div>
            <span className="md-a-name">{a.label}</span>
          </div>
        ))}
        {absent.map(a => (
          <div key={a.key} className="md-allergen-item">
            <div className="md-a-badge md-a-no">–</div>
            <span className="md-a-name">{a.label}</span>
          </div>
        ))}
      </div>
      <div className="md-allergen-legend">
        <span><span className="md-a-badge md-a-yes" style={{display:'inline-flex'}}>✓</span> Contains</span>
        <span><span className="md-a-badge md-a-may" style={{display:'inline-flex'}}>M</span> May contain</span>
        <span><span className="md-a-badge md-a-no" style={{display:'inline-flex'}}>–</span> Not present</span>
      </div>
      {note && <div className="md-allergen-note">{note}</div>}
    </div>
  );
};

const MealDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [meal, setMeal]       = useState(null);
  const [matrix, setMatrix]   = useState([]);
  const [note, setNote]       = useState('');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    Promise.all([
      getDoc(doc(db, 'haf_content', 'menu')),
      getDoc(doc(db, 'haf_content', 'allergens')),
    ]).then(([menuSnap, allergenSnap]) => {
      const items = menuSnap.exists() ? (menuSnap.data().items || []) : [];
      const found = items.find(i => toSlug(i.name) === slug);
      if (!found) { setNotFound(true); return; }
      setMeal(found);
      if (allergenSnap.exists()) {
        setMatrix(allergenSnap.data().matrix || []);
        setNote(allergenSnap.data().note || '');
      }
    }).catch(e => {
      console.warn('MealDetail load error:', e.message);
      setNotFound(true);
    }).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="md-page">
      <div className="md-loading">
        <div className="md-spinner" />
        <p style={{ fontFamily:'Open Sans,sans-serif', color:'#4a7c2f', marginTop: 8 }}>Loading…</p>
      </div>
    </div>
  );

  if (notFound) return (
    <div className="md-page">
      <nav className="md-nav">
        <button className="md-back" onClick={() => navigate('/staging')}>
          <span className="md-back-arrow">←</span> Back to Menu
        </button>
        <div className="md-nav-logo" onClick={() => navigate('/staging')}>
          <img src={logo} alt="Akshaya Patra UK" />
        </div>
      </nav>
      <div className="md-loading">
        <p style={{ fontFamily:'Oswald,sans-serif', fontSize:'1.4rem', color:'#1d3461' }}>Meal not found.</p>
        <button className="md-order-btn" style={{width:'auto',padding:'12px 28px',marginTop:16,background:'#4a7c2f',color:'#fff'}} onClick={() => navigate('/staging')}>
          Back to Home
        </button>
      </div>
    </div>
  );

  const story = meal.story || meal.desc || meal.description || '';
  const allergenRow = matrix.find(r => toSlug(r.meal) === toSlug(meal.name || ''));

  return (
    <div className="md-page">

      {/* NAV */}
      <nav className="md-nav">
        <button className="md-back" onClick={() => navigate('/staging#haf-menu')}>
          <span className="md-back-arrow">←</span> Back to Menu
        </button>
        <div className="md-nav-logo" onClick={() => navigate('/staging')}>
          <img src={logo} alt="Akshaya Patra UK" />
        </div>
      </nav>

      {/* HERO */}
      <div className="md-hero">
        {meal.image_url
          ? <img src={meal.image_url} className="md-hero-img" alt={meal.name} />
          : <div className="md-hero-ph">🍽</div>
        }
        <div className="md-hero-overlay">
          <div className="md-breadcrumb">
            Menu <span>/</span> {meal.name}
          </div>
          <h1 className="md-hero-title">{meal.name}</h1>
          {meal.tag && <div className="md-hero-tag">{meal.tag}</div>}
        </div>
      </div>

      {/* BODY */}
      <div className="md-body">

        {/* LEFT: Story + Recipe */}
        <div className="md-left">

          {/* Story */}
          <div className="md-story-section">
            <div className="md-section-h">The Story</div>
            <div className="md-story">
              {story.split('\n').filter(Boolean).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>

          {/* Ingredients — allergens auto-bolded */}
          <IngredientsList ingredients={meal.ingredients} allergenRow={allergenRow} />

          {/* Recipe — only shown if admin has populated it */}
          {meal.recipe && (
            <div className="md-recipe-section">
              <div className="md-section-h">Recipe</div>
              <div className="md-recipe-text">{meal.recipe}</div>
            </div>
          )}

          {/* Order CTA — desktop only, in left column */}
          <div className="md-order-cta">
            <p>This meal is part of our HAF programme — available to eligible children during school holidays.</p>
            <button className="md-order-btn" onClick={() => navigate('/login')}>
              Login / Order Now
            </button>
          </div>

        </div>

        {/* RIGHT: Nutrition + Allergens */}
        <div className="md-right">
          <NutritionBox nutrition={meal.nutrition} />
          <AllergenBox matrix={matrix} mealName={meal.name} note={note} />
        </div>

      </div>

      {/* FOOTER */}
      <footer style={{ background:'#1d3461', color:'rgba(255,255,255,.5)', textAlign:'center', padding:'24px 20px', fontSize:'.78rem', fontFamily:"'Open Sans', sans-serif" }}>
        © {new Date().getFullYear()} Akshaya Patra UK — Holiday Activities & Food Programme
      </footer>

    </div>
  );
};

export default MealDetail;
